import { css } from '@emotion/react';
import {
  faCheck, faCopy, faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { type FC } from 'react';
import { ButtonComponent } from '~/_shared/baseComponents/buttons/button/button.component';
import { ButtonStyle } from '~/_shared/baseComponents/buttons/button/button.types';
import { LinkComponent } from '~/_shared/baseComponents/links';
import {
  TooltipBehavior, TooltipComponent,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import { SpreadsheetCellSelectDisabledReason } from '~/_shared/components/spreadsheet/spreadsheet.component';
import {
  type SpreadsheetHeaderColumn, type SpreadsheetRow,
} from '~/_shared/components/spreadsheet/spreadsheet.types';
import {
  SAMPLE_SPREADSHEET_FILE_NAME, SampleSpreadsheetUrl,
} from '~/_shared/constants/sampleData';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { type ThemeProps } from '~/_shared/types/themeProps';
import { downloadFile } from '~/_shared/utils/document/document.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { type ModalProps } from '~/modal/modalType.enum';
import {
  s, Trans,
} from '~/translations/Trans';
import { DataTableComponent } from '../table/dataTable.component';

const dataTableStyle = css({
  height: '100%',
  width: '100%',
  boxSizing: 'border-box',
  padding: '0 20px 20px',
  minHeight: 0,
});

const headerStyle = css({
  display: 'flex',
  padding: '16px 40px',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 10,
});

const buttonWrapperStyle = css({
  flexShrink: 0,
});

const linkStyle = css({
  fontSize: 'inherit',
  textDecoration: 'underline',
});

const wrapperStyle = css({
  height: '80vh',
  display: 'flex',
  flexDirection: 'column',
});

const modalContentStyle = css({
  display: 'none',
});

const footerStyle = ({ theme }: ThemeProps) => css({
  borderTop: `1px solid ${theme.borderColors.primary}`,
  padding: '10px 16px',
  display: 'flex',
  justifyContent: 'space-between',
});

type BadDataModalProps = ModalProps<{
  columnWidthsLocalStorageKey: string;
  columns: SpreadsheetHeaderColumn[];
  isLoading: boolean;
  isRemapButtonDisabled: boolean;
  showCopyToClipboardTooltip: boolean;
  spreadsheetDataRows: SpreadsheetRow[];
  spreadsheetDisabledReason?: SpreadsheetCellSelectDisabledReason;

  onCopyToClipboardClick: () => void;
  onDataChange: (newData: SpreadsheetRow[]) => void;
  onDiscardUnmappedRowsClick?: () => void;
  onRemapDataRowsClick: () => void;
}>;

export const BadDataModalComponent: FC<BadDataModalProps> = (props) => {
  const [t] = useTranslation();
  const theme = useTheme();

  const dataTableSpreadsheetProps = {
    data: props.spreadsheetDataRows,
    onDataChange: props.onDataChange,
    getSpreadsheetCellDisabledMessage:
      props.spreadsheetDisabledReason === SpreadsheetCellSelectDisabledReason.SalesforceMap
        ? () => t('salesforce.spreadsheetData.cannotEditInApp')
        : undefined,
    columns: props.columns,
    columnWidthsLocalStorageKey: props.columnWidthsLocalStorageKey,
    cellSelectConfig: {
      isEnabled: false,
      disabledReason: props.spreadsheetDisabledReason || SpreadsheetCellSelectDisabledReason.Default,
    },
  };

  return (
    <ModalComponent
      {...props}
      preventCloseOnOverlayClick
      caption={t('The unmapped data below was removed from your imported dataset')}
      contentStyle={modalContentStyle}
      maxWidth="90vw"
      additionalContent={(
        <div css={wrapperStyle}>
          <div css={headerStyle}>
            <div>
              <Trans
                i18nKey="Data should contain location information such as Address, City, State, Zip/Postal Code.<1></1>You may also check that Step 2, Match Up Your Data, was completed, and/or download our <3>spreadsheet template</3> for help with formatting issues."
              >
                {s('Data should contain location information such as Address, City, State, Zip/Postal Code.')}
                <br />
                {s('You may also check that Step 2, Match Up Your Data, was completed, and/or download our ')}
                <LinkComponent
                  css={linkStyle}
                  path="#"
                  onClick={() => {
                    downloadFile(SampleSpreadsheetUrl, { fileName: SAMPLE_SPREADSHEET_FILE_NAME });
                  }}
                >
                  spreadsheet template
                </LinkComponent>
                {s(' for help with formatting issues.')}
              </Trans>
            </div>

            <div css={buttonWrapperStyle}>
              <TooltipComponent
                tooltipContent={props.showCopyToClipboardTooltip ? t('Copied to Clipboard!') : null}
                behavior={TooltipBehavior.ShowAlways}
              >
                <ButtonComponent
                  buttonStyle={ButtonStyle.Tertiary}
                  text={t('Copy to Clipboard')}
                  prefixIcon={faCopy}
                  onClick={props.onCopyToClipboardClick}
                />
              </TooltipComponent>
            </div>
          </div>

          <DataTableComponent
            css={dataTableStyle}
            spreadsheetProps={dataTableSpreadsheetProps}
            isSearchFilterActive={false}
            isLoading={props.isLoading}
          />

          <div css={footerStyle({ theme })}>
            {props.onDiscardUnmappedRowsClick && (
              <ButtonComponent
                text={t('Discard Unmapped Rows')}
                buttonStyle={ButtonStyle.Danger}
                prefixIcon={faTrash}
                onClick={props.onDiscardUnmappedRowsClick}
              />
            )}
            <ButtonComponent
              isDisabled={props.isRemapButtonDisabled}
              text={t('Remap Data Rows')}
              prefixIcon={faCheck}
              onClick={props.onRemapDataRowsClick}
            />
          </div>
        </div>
      )}
    >
      {null}
    </ModalComponent>
  );
};
